import React, { useEffect, useRef } from "react";
import {
  FIDUCIAL_FAMILY,
  FIDUCIAL_GRID_SIZE,
  fiducialCellIsDark,
  getTagMatrix
} from "../../utils/session-fiducial/session-fiducial";
import "./session-fiducial.scss";

// SessionFiducial renders a small AprilTag (tag36h11) fiducial encoding the
// current question-id — via the per-session id-book — into a fixed screen
// corner. Because the tutor screen-shares the question view, the mark is
// captured by Zoom's recording with zero tutor action, letting the offline
// decoder index the session audio by question. See
// utils/session-fiducial/session-fiducial.js for the decoder contract.
//
// Geometry matches F3's overlay.py reference renderer + Phase-0 findings:
//   - tagPx = the tag36h11 side in px (F3 DEFAULT_TAG_PX = 140; doc-recommended
//     120-180px; Phase-0 survivable floor ~48px @1080p);
//   - quiet zone = tagPx / 8 (F3 QUIET_ZONE_FRAC = 8);
//   - opacity = 0.55 (F3 DEFAULT_ALPHA; F3's overlay expects the frontend to
//     set ~0.55 via CSS). All three are props so the Phase-0 re-encode sweep
//     can retune without a code change.
const QUIET_ZONE_FRAC = 8; // quiet-zone border = tagPx / QUIET_ZONE_FRAC (F3)

const quietZonePx = (tagPx) => Math.max(4, Math.floor(tagPx / QUIET_ZONE_FRAC));

const SessionFiducial = ({
  questionId,
  idBook,
  enabled = true,
  corner = "bottom-right",
  tagPx = 140,
  opacity = 0.55
}) => {
  const canvasRef = useRef(null);

  // Resolve (and register, so it lands in the emitted id-book) the tag index.
  const tagIndex =
    enabled && idBook && questionId !== undefined && questionId !== null
      ? idBook.register(questionId)
      : null;
  const matrix = tagIndex === null ? null : getTagMatrix(tagIndex);

  const quiet = quietZonePx(tagPx);
  const totalPx = tagPx + 2 * quiet;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !matrix) {
      return;
    }
    // jsdom / very old browsers may not implement 2D canvas; degrade quietly
    // (jsdom's getContext throws "Not implemented" rather than returning null).
    let ctx = null;
    try {
      ctx = typeof canvas.getContext === "function" ? canvas.getContext("2d") : null;
    } catch (_) {
      ctx = null;
    }
    if (!ctx) {
      return;
    }

    const grid = FIDUCIAL_GRID_SIZE;
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
    const cell = tagPx / grid;

    canvas.width = Math.round(totalPx * dpr);
    canvas.height = Math.round(totalPx * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;

    // Opaque white quiet zone (= tagPx/8) around the black tag square — the
    // detector needs a lighter border, and opaque keeps downscale/JPEG blocking
    // from bleeding the page behind it into the border.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, totalPx, totalPx);

    for (let row = 0; row < grid; row += 1) {
      const rowInt = matrix[row];
      for (let col = 0; col < grid; col += 1) {
        if (fiducialCellIsDark(rowInt, col, grid)) {
          ctx.fillStyle = "#000000";
          // sub-device-px overdraw avoids hairline seams between cells at
          // fractional cell sizes (which the decoder would misread).
          ctx.fillRect(
            quiet + col * cell,
            quiet + row * cell,
            cell + 0.75 / dpr,
            cell + 0.75 / dpr
          );
        }
      }
    }
  }, [matrix, tagPx, quiet, totalPx]);

  if (!matrix) {
    return null;
  }

  return (
    <div
      className={`session-fiducial session-fiducial--${corner}`}
      style={{ opacity }}
      data-testid="session-fiducial"
      data-fiducial-family={FIDUCIAL_FAMILY}
      data-fiducial-tag-index={tagIndex}
      aria-hidden="true"
    >
      <canvas
        ref={canvasRef}
        width={totalPx}
        height={totalPx}
        style={{ width: `${totalPx}px`, height: `${totalPx}px`, display: "block" }}
      />
    </div>
  );
};

export default SessionFiducial;
